<template>
    <tailwind-layout title="Resources">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto flex justify-between items-center">
                <h1 class="font-semibold text-2xl leading-tight">Resources</h1>
            </div>
        </template>
        <div class="overflow-x-auto">
            <div class="flex flex-col">
                <div class="overflow-x-auto">
                    <div class="align-middle inline-block min-w-full">
                        <div class="overflow-hidden">
                            <ul
                                role="list"
                                class="my-5 grid grid-cols-1 gap-6 px-2 sm:grid-cols-2 lg:grid-cols-2"
                            >
                                <li
                                    v-for="resource in allResources"
                                    :key="resource.title"
                                    class="col-span-1 flex flex-col rounded-lg border border-gray-500 bg-white p-4 shadow transition-shadow hover:shadow-md"
                                >
                                    <div class="flex min-h-full flex-1 flex-col">
                                        <div class="flex flex-1 items-start gap-3">
                                            <font-awesome-icon
                                                :icon="resource.icon"
                                                size="2x"
                                                class="shrink-0 rounded-md bg-rent-risk-blue-400 p-2 text-white"
                                                aria-hidden="true"
                                            />
                                            <div class="min-w-0 flex-1 text-left">
                                                <h2 class="text-xl font-bold text-gray-900">
                                                    {{ resource.title }}
                                                </h2>
                                                <p
                                                    v-if="resource.description"
                                                    class="mt-2 text-sm text-gray-500"
                                                >
                                                    {{ resource.description }}
                                                </p>
                                            </div>
                                        </div>
                                        <div class="mt-4">
                                            <button
                                                v-if="resource.link"
                                                type="button"
                                                class="primaryButton w-full"
                                                @click="downloadFile(resource.link, resource.title)"
                                            >
                                                <span v-if="resource.button_text">{{ resource.button_text }}</span>
                                                <span v-else>View File</span>
                                            </button>
                                            <button
                                                v-else
                                                type="button"
                                                class="primaryButton w-full disabled:cursor-not-allowed disabled:opacity-50"
                                                :disabled="!isChatLoaded"
                                                @click="openHubSpotChat"
                                            >
                                                Chat Now!
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            </ul>
                            <div class="mt-8 overflow-hidden rounded-lg border border-gray-500 bg-white shadow">
                                <h2 class="border-b border-gray-200 px-6 py-4 text-left text-xl font-bold text-gray-900">
                                    State-Specific Tenant Screening Compliance
                                </h2>
                                <us-map></us-map>

                                <div class="border-t border-gray-200 p-6">
                                    <div class="space-y-4">
                                        <div class="text-lg text-gray-800 font-semibold">
                                            <h6 class="italic text-gray-600">
                                                Click on a state to find out more information. On mobile devices you can zoom in and press states. Zoom out to read the information in the black box below the map.
                                            </h6>
                                        </div>
                                        <p class="text-gray-700 italic">
                                            <strong class="font-semibold">*Note:</strong> Seek legal counsel if you have specific questions regarding tenant application fees and regulations. This information is not exhaustive and should not replace the guidance of an attorney. RentRisk is not responsible or liable for leasing decisions made by the owner, landlord or real estate agent.
                                        </p>
                                        <p class="text-gray-700 mt-4">
                                            <span class="font-semibold">Two-Step Process Explained:</span> For criminal background assessments: Some states, counties, and the District of Columbia prohibit early-stage inquiries into criminal histories when evaluating a prospective tenant’s application.
                                        </p>
                                        <ol class="list-decimal pl-6 text-gray-700 space-y-2 mt-4 ml-5">
                                            <li>Landlords cannot disregard a tenant based solely on a past criminal record.</li>
                                            <li>Landlords should only perform comprehensive background checks after a conditional acceptance is offered. Once a conditional acceptance has been offered to a tenant based on income verification and references check, then an inquiry into a criminal background can be made.</li>
                                            <li>In the cases where disqualifying offenses are determined in the process, offers for tenancy can be rescinded.</li>
                                        </ol>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </tailwind-layout>
</template>

<script setup>
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import UsMap from '../../Shared/UsMap.vue';
import {
    faClipboardListCheck,
    faFaceMonocle,
    faComments,
    faQuestionCircle,
    faPersonChalkboard,
    faTriangleExclamation,
} from "@fortawesome/pro-light-svg-icons";
import { ref, onMounted, computed } from 'vue';

const iconMap = {
    faClipboardListCheck,
    faFaceMonocle,
    faComments,
    faQuestionCircle,
    faPersonChalkboard,
    faTriangleExclamation,
};

const props = defineProps({
    resources: Array,
});

const staticResources = [
    {
        'title' : 'Need More Help?',
        'description' : 'Contact one of our support team today.',
        'link' : 'https://rentrisk.com/contact/',
        'button_text' : 'Contact Us',
        'icon' : faQuestionCircle,
    },
    {
        'title' : 'Need to chat right now?',
        'description' : 'Click the button below to chat with one of our support team members.',
        'link' : '',
        'button_text' : '',
        'icon' : faComments,
    }
];

const allResources = computed(() => [
    ...props.resources.map(r => ({ ...r, icon: iconMap[r.icon] ?? faQuestionCircle })),
    ...staticResources,
]);

// HubSpot Chat State
const isChatLoaded = ref(false);

// HubSpot Chat Functions
const checkHubSpotLoaded = () => {
  if (window.HubSpotConversations && window.HubSpotConversations.widget) {
    isChatLoaded.value = true;
  } else {
    setTimeout(checkHubSpotLoaded, 500);
  }
};

const openHubSpotChat = () => {
  if (window.HubSpotConversations && window.HubSpotConversations.widget) {
    window.HubSpotConversations.widget.open();
  } else {
    alert('HubSpot chat widget not loaded yet.');
  }
};

// Initialize HubSpot check on mount
onMounted(checkHubSpotLoaded);

// Download State
const isDownloading = ref(false);

// Open File in New Tab Function
const downloadFile = (url, name) => {
  try {
    // Open file in new tab
    window.open(url, '_blank');
  } catch (error) {
    alert('Failed to open file. Please try again.');
  }
};

</script>
